import { z } from "zod/v3";
import { PlainMessage, Timestamp } from "@bufbuild/protobuf";
import { createSchema, ZodOutputFor } from "../zod-utils";
import {
  Action as ProtoAction,
  Action_Changeability,
  Action_Orientation,
} from "@opstel/gen/knop/ui/v1/model/action_pb";
import { DateTime } from "luxon";
import { MOVEMENT_ACTION_TYPES, SINGLE_TRACK_ACTION_TYPES } from "../v2/models/actionType";
import { TrackCode } from "./track";
import { Length } from "@shared/common/utils/length";
import {
  Changeability,
  Orientation,
  Action,
  SingleTrackAction,
  MovementAction,
} from "@opstel/query/v2/models/action";

type ValidatedChangeability = Exclude<Action_Changeability, Action_Changeability.UNKNOWN>;
function transformChangeabilityEnum(changeability: ValidatedChangeability): Changeability {
  const mapping: { [key in ValidatedChangeability]: Changeability } = {
    [Action_Changeability.FIXED]: "Fixed",
    [Action_Changeability.PINNED]: "Pinned",
    [Action_Changeability.OPEN]: "Open",
  };
  return mapping[changeability];
}
type ValidatedOrientation = Exclude<Action_Orientation, Action_Orientation.ORIENTATION_UNKNOWN>;
function transformOrientationEnum(orientation: ValidatedOrientation): Orientation {
  const mapping: { [key in ValidatedOrientation]: Orientation } = {
    [Action_Orientation.FIRST_UNIT_ON_TARGET_SIDE]: "FirstUnitOnTargetSide",
    [Action_Orientation.FIRST_UNIT_ON_SOURCE_SIDE]: "FirstUnitOnSourceSide",
  };
  return mapping[orientation];
}
type WithInterval = { startTime: DateTime; endTime: DateTime };
const refineTimes: [(item: WithInterval) => boolean, { message: string }] = [
  (item) => item.startTime <= item.endTime,
  { message: "Start time should not be after end time" },
];

const baseActionSchema = {
  guid: z.string().uuid({ message: "Action guid should be a valid uuid" }),
  name: z.string(),
  shuntingUnitGuid: z.string(),
  startTime: z
    .instanceof(Timestamp, { message: "Action should have a start time" })
    .transform((val) => DateTime.fromJSDate(val.toDate()))
    .refine((d) => d.isValid, "Invalid timestamp"),
  endTime: z
    .instanceof(Timestamp, { message: "Action should have a end time" })
    .transform((val) => DateTime.fromJSDate(val.toDate()))
    .refine((d) => d.isValid, "Invalid timestamp"),
  changeability: z
    .nativeEnum(Action_Changeability)
    .refine((val) => val !== Action_Changeability.UNKNOWN, {
      message: "Action changeability should not be unknown",
    })
    .transform(transformChangeabilityEnum),
  orientation: z
    .nativeEnum(Action_Orientation)
    .refine((val) => val !== Action_Orientation.ORIENTATION_UNKNOWN, {
      message: "Action orientation should not be unknown",
    })
    .transform(transformOrientationEnum),
};

const trackActionSchema = z
  .object({
    ...baseActionSchema,
    actionType: z.enum(SINGLE_TRACK_ACTION_TYPES),
    distanceToSourceInMm: z.number(),
    stablingInfra: z.string().min(1, { message: "TrackAction stabling infra should not be empty" }),
  })
  .refine(...refineTimes)
  .transform(({ distanceToSourceInMm, ...action }) => ({
    distanceToSource: new Length(distanceToSourceInMm),
    ...action,
  }))
  .transform((action) => ({
    id: action.guid,
    shuntingUnitId: action.shuntingUnitGuid,
    ...action,
  }))
  .transform((action) => {
    return { ...action, type: action.actionType } satisfies SingleTrackAction;
  }) satisfies ZodOutputFor<SingleTrackAction>;

const movementActionSchema = z
  .object({
    ...baseActionSchema,
    actionType: z.enum(MOVEMENT_ACTION_TYPES),
    tracks: z
      .array(z.string().min(1, { message: "MovementAction track code should not be empty" }))
      .min(1, { message: "MovementAction tracks should not be empty" }),
    distanceToSourceInMmStart: z.number(),
    distanceToSourceInMmEnd: z.number(),
    stablingInfraStart: z
      .string()
      .min(1, { message: "MovementAction stabling infra begin should not be empty" }),
    stablingInfraEnd: z
      .string()
      .min(1, { message: "MovementAction stabling infra end should not be empty" }),
  })
  .refine(...refineTimes)
  .transform((action) => ({
    ...action,
    id: action.guid,
    shuntingUnitId: action.shuntingUnitGuid,
    type: action.actionType,
  }))
  .transform(({ distanceToSourceInMmStart, distanceToSourceInMmEnd, ...action }) => ({
    ...action,
    isNetworkMovement: action.tracks.find(TrackCode.isForeignTrack) !== undefined,
    distanceToSourceStart: new Length(distanceToSourceInMmStart),
    distanceToSourceEnd: new Length(distanceToSourceInMmEnd),
  })) satisfies ZodOutputFor<MovementAction>;

interface IntermediateAction {
  action:
    | { case: "trackAction"; value: SingleTrackAction }
    | { case: "movement"; value: MovementAction };
}
export const actionSchema = createSchema<IntermediateAction, PlainMessage<ProtoAction>>({
  action: z.discriminatedUnion("case", [
    z.object({ case: z.literal("trackAction"), value: trackActionSchema }),
    z.object({ case: z.literal("movement"), value: movementActionSchema }),
  ]),
}).transform((union) => union.action.value) satisfies ZodOutputFor<Action>;

export function parseAction(proto: PlainMessage<ProtoAction>): Action {
  return actionSchema.parse(proto);
}
